import React, { useState, useEffect, useRef } from "react";
import { createRoot } from "react-dom/client";
import { cqAPI } from "../../lib/Auth";
import {
  ChakraProvider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  Box,
  Text,
  HStack,
  VStack,
  Divider,
  Switch,
  useToast,
  Select,
  IconButton,
  Spinner,
  Spacer,
  Flex
} from '@chakra-ui/react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import dialogManager from "./DialogManager";

let terminalInstance = null;

const Camelize = (st) => st.replace(/(^.)/, (m) => m.toUpperCase());
// eslint-disable-next-line no-undef
const IS_DEV_DEBUG = process.env.NODE_ENV === "development" && process.env.REACT_APP_DEBUG;

let stripeTerminalLoaded = false;
const loadStripeTerminal = () => {
  return new Promise((resolve, reject) => {
    if (stripeTerminalLoaded) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = "https://js.stripe.com/terminal/v1/";
    script.onload = () => {
      stripeTerminalLoaded = true;
      resolve();
    };
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

const CollectPaymentMethodModal = ({ isOpen, onClose, onCancel }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Process Payment</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text>Please run the card on the card reader.</Text>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="red" onClick={onCancel}>Cancel</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const CCTestOptions = ({isAppInTestDebugMode, isPaymentTestEnabled, onPaymentChange}) => {
  if(!IS_DEV_DEBUG || !isAppInTestDebugMode) {
    return null;
  }
  const paymentResults = [
    {testCardNumber: '4242424242424242', description: 'succeeded'},
    {testCardNumber: '4000000000000002', description: 'declined'},
    {testCardNumber: '4000000000000069', description: 'expired card'},
    {testCardNumber: '4000000000009995', description: 'insufficient funds'},
    {testCardNumber: '4000000000000119', description: 'processing error'},
    {testCardNumber: '4000000000000036', description: 'postal code wrong'},
    {testCardNumber: '4100000000000019', description: 'blocked'},
    {testCardNumber: '4000000000000259', description: 'fraudulent'},
    {testCardNumber: '4000000000007726', description: 'async refund'},
  ];
  return (
    <Flex direction="row" width="100%">
      <Spacer />
      <Select
        placeholder='Select payment result'
        onChange={onPaymentChange}
        width="auto"
        visibility={isPaymentTestEnabled ? "visible" : "hidden"}
        border="2px solid"
        borderColor="orange.400"
        focusBorderColor="orange.400"
      >
        {paymentResults.map((paymentResult) => (
          <option key={paymentResult.testCardNumber} value={paymentResult.testCardNumber}>
            {paymentResult.description}
          </option>
        ))}
      </Select>
    </Flex>);
};

const CreditCardModal = ({
  isOpen,
  onClose,
  onSave,
  userProfileName,
  initialSource,
  _uid,
  onCardChange,
  onSubmit,
  showSourceOnlyForInstallment,
  hideInstallment,
  payButtonText = "Pay",
  hideFSA = false,
  readers,
  isTestMode = false,
}) => {
  const [description, setDescription] = useState("Dental Payment");
  const [amount, setAmount] = useState(0);
  const [tax, setTax] = useState(0);
  const [installment, setInstallment] = useState(false);
  const [highRisk, setHighRisk] = useState(false);
  const [last4, setLast4] = useState(initialSource ? initialSource.last4 : "");
  const [downPayment, setDownPayment] = useState(0);
  const [installmentCount, setInstallmentCount] = useState(1);
  const [treatmentStartDate, setTreatmentStartDate] = useState("");
  const [eachPayment, setEachPayment] = useState(0);
  const [total, setTotal] = useState(0);
  const [totalTax, setTotalTax] = useState("0.00");
  const [sourceType, setSourceType] = useState(initialSource?.type);
  const [selectedSource, setSelectedSource] = useState((readers.find(r => r.default) || readers[0])?.id || "card");
  const [sourceTypes, setSourceTypes] = useState(
    readers.concat(
      initialSource?.type
        ? [
            {
              last4: initialSource?.last4,
              type: initialSource?.type,
            },
          ]
        : []
    )
  );
  const [paymentFrequency, setPaymentFrequency] = useState("weekly");
  const toast = useToast();
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [isCollectPaymentMethodModalOpen, setIsCollectPaymentMethodModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);  // New state for loading
  const [isPaymentTestEnabled, setIsPaymentTestEnabled] = useState(false);
  const [testCardNumber, setTestCardNumber] = useState('4242424242424242');
  const currentReaderRef = useRef(null);
  const discoveredReaders = useRef([]);

  useEffect(() => {
    const calculateTotal = () => {
      const taxAmount = parseFloat(amount || 0) * ((isNaN(tax) ? 0 : tax) / 100);
      setTotalTax(taxAmount.toFixed(2));

      const totalAmount = parseFloat(amount || 0) * (1 + (isNaN(tax) ? 0 : tax) / 100);
      setTotal(totalAmount.toFixed(2));
    };

    const calculateEachPayment = () => {
      const totalPayment = parseFloat(total);
      const leftAmount = totalPayment - parseFloat(downPayment);
      setEachPayment((leftAmount / parseInt(installmentCount)).toFixed(2));
    };

    calculateTotal();

    if (installment) {
      calculateEachPayment();
    }
  }, [amount, tax, downPayment, installmentCount, installment, total]);

  useEffect(() => {
    if (onCardChange) {
      onCardChange({ type: sourceType, last4 });
    }
  }, [sourceType, last4]);

  useEffect(() => {
    if (readers.length > 0) {
      loadStripeTerminal()
        .then(() => {
          // Initialize terminal here
          function unexpectedDisconnect() {
            console.log("Disconnected from reader");
          }

          function fetchConnectionToken() {
            return cqAPI.post('/payments/connection_token').then(r => r.data);
          }

          function handleConnectionStatusChange(newStatus) {
            if (newStatus.status === 'not_connected') {
              currentReaderRef.current = null;
            }
          }

          // eslint-disable-next-line no-undef
          terminalInstance = terminalInstance || StripeTerminal.create({
            onFetchConnectionToken: fetchConnectionToken,
            onUnexpectedReaderDisconnect: unexpectedDisconnect,
            onConnectionStatusChange: handleConnectionStatusChange,
          });
          setSelectedSource((readers.find(r => r.default) || readers[0])?.id);

        })
        .catch((error) => {
          console.error("Error loading Stripe Terminal library:", error);
        });
    }
  }, [readers]);

  // Enable payment selection if simulated terminal is chosen
  useEffect(() => {
    if (IS_DEV_DEBUG && isTestMode && readers.length > 0) {
      const reader = readers.find((reader) => reader.id === selectedSource);
      if (reader?.device_type.startsWith('simulated')) {
        setIsPaymentTestEnabled(true);
      } else {
        setIsPaymentTestEnabled(false);
      }
    }
  }, [selectedSource, readers])

  const showToast = (message, title="Validation Error", status="error") => {
    toast({
      title,
      description: message,
      status,
      duration: 9000,
      isClosable: true,
    });
  };

  function selectTerminalReader(readers) {
    const reader = readers.find(r => r.id === selectedSource);
    if (!reader) {
      showToast("Reader selected could not be found")
    }
    return reader;
  }

  function discoverReaderHandler() {
    const terminal = terminalInstance
    // Don't rediscover readers if we already did so
    if (discoveredReaders.current.length > 0 && !isPaymentTestEnabled) {
      return Promise.resolve(selectTerminalReader(discoveredReaders.current));
    }

    let config = { simulated: false };
    if (isPaymentTestEnabled) {
      config.simulated = true;
    }
    return terminal.discoverReaders(config).then(function (discoverResult) {
      if (discoverResult.error) {
        showToast(discoverResult.error.message);
        return undefined;
      } else if (discoverResult.discoveredReaders.length === 0) {
        showToast("Reader is not available");
        return undefined;
      } else {
        let reader
        if (isPaymentTestEnabled) {
          reader = discoverResult.discoveredReaders[0];
        } else {
          // Save the readers discovered
          discoveredReaders.current = discoverResult.discoveredReaders;
          reader = selectTerminalReader(discoverResult.discoveredReaders)
        }
        return reader;
      }
    });
  }

  const processInPersonPayment = async () => {
    const terminal = terminalInstance

    // HW card reader should be selected
    const selectedReader = await discoverReaderHandler();
    if (!selectedReader) { return; }
    setIsLoading(true);

    // Connect to reader if it's a different one or if we have not already connected
    if (currentReaderRef.current === null ||
      currentReaderRef.current.id !== selectedReader.id ||
      terminal.getConnectionStatus() !== 'connected') {
      // Disconnect from old reader
      if (currentReaderRef.current || terminal.getConnectionStatus() === 'connected') {
        await terminal.disconnectReader().catch((error)=> {
          console.error('Failed to disconnect reader:', error.message);
        });
        currentReaderRef.current = null;
      }
      try {
        const connectResult = await terminal.connectReader(selectedReader);
        if (connectResult.error) {
          showToast(connectResult.error.message);
          setIsLoading(false);
          return;
        }
      } catch (error) {
        showToast(`Unable to connect to reader: ${error.message}`);
        setIsLoading(false);
        return;
      }
      currentReaderRef.current = selectedReader;
    }

    let response = await cqAPI.post(`/payments/intent`, {
      uid: _uid,
      desc: description,
      amount: total
    });

    if (response.type === 'error') {
      showToast('Payment issue: ' + response.msg);
      setIsLoading(false);
      return;
    }

    setIsCollectPaymentMethodModalOpen(true);
    if (isPaymentTestEnabled) {
      console.log('Running test card:', testCardNumber);
      terminal.setSimulatorConfiguration({testCardNumber});
    }
    const result = await terminal.collectPaymentMethod(response.data);
    if (result.error) {
      showToast('Getting payment method failed: ' + (result.error?.message ?? "Something Went Wrong! Try Again." ));
      setIsCollectPaymentMethodModalOpen(false);
      setIsLoading(false);
      return;
    }

    const payment = await terminal.processPayment(result.paymentIntent);
    if (payment.error) {
      await cqAPI.post(`/payments/cancel_intent`, {
        payment_intent_id: result.paymentIntent.id,
      });
      showToast('Process payment failed: ' + (payment.error?.message ?? "Something Went Wrong! Try Again."));
      setIsCollectPaymentMethodModalOpen(false);
      setIsLoading(false);
      return;
    }
    
    async function finalize(save){
      response = await cqAPI.post(`/setting/card?_uid=${_uid}`, {
        paymentMethodId: payment.paymentIntent.charges.data[0].payment_method_details.card_present.generated_card,
        connect : true,
        save
      });
      if (response.type === 'ok' && save){
        setLast4(payment.paymentIntent.charges.data[0].payment_method_details.card_present.last4);
        setSourceType(payment.paymentIntent.charges.data[0].payment_method_details.card_present.funding);
        setSourceTypes(readers.concat([
          {
            last4: payment.paymentIntent.charges.data[0].payment_method_details.card_present.last4,
            type: payment.paymentIntent.charges.data[0].payment_method_details.card_present.funding
          },
        ]));
      }

      response = await cqAPI.post('/payments/capture', {
        payment_intent_id: payment.paymentIntent.id
      });
      
      if (response.type === 'error') {
        showToast('Issue getting payment: ' + response.msg);
        setIsLoading(false);
        return;
      }
      
      dialogManager.open({
        onDeny: () => {
        },
        acceptText: "OK",
        body: `The payment of $${total} was successfully captured.`,
        header: "Success",
      });

      setIsCollectPaymentMethodModalOpen(false);
      setIsLoading(false);
      await terminal.disconnectReader();

      onSave && onSave(response);
      onClose();
    }
    
    dialogManager.open({
      onDeny: () => {
        finalize(false)
      },
      onAccept: () => {
        finalize(true)
      },
      acceptText: "Yes",
      denyText: "No",
      isClosable : false,
      body: "Payment is about to be captured. Would you like to store this card for the patient?",
      header: "Confirm",
    });
  };

  const cancelCollectSetupIntentPaymentMethod = async () => {
    const terminal = terminalInstance
    await terminal.cancelCollectPaymentMethod();
    await terminal.disconnectReader();
    setIsCollectPaymentMethodModalOpen(false);
    setIsLoading(false);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!description) {
      showToast("Description is required.");
      return;
    }

    if (!amount || isNaN(amount) || parseFloat(amount) <= 0) {
      showToast("Amount should be more than 0 and numeric.");
      return;
    }

    if (installment) {
      if (!highRisk && (!treatmentStartDate || parseFloat(downPayment) <= 0)) {
        showToast(
          "Treatment Start Date and Down Payment are required for low risk installment payments."
        );
        return;
      }
    }

    if (
      ((installment && !highRisk) || !showSourceOnlyForInstallment) &&
      !last4
      && (!selectedSource.startsWith('tmr_'))
    ) {
      showToast("Please add a source for this payment.");
      return;
    }
    if (isNaN(tax) || tax < 0 || tax > 100){
      showToast("Tax should be between 0 and 100.");
      return;
    }

    const paymentType = installment
      ? highRisk
        ? `paylater`
        : paymentFrequency
      : "onetime";

    if (paymentType === 'onetime' && selectedSource !== 'card' && payButtonText !== 'Attach' && !paymentMethod) {
      await processInPersonPayment();
      return;
    }

    setIsLoading(true);  // Start loading

    const param = {
      amount: total,
      desc: description,
      type: paymentType,
      treatmentdate: formatDate(treatmentStartDate),
      downpayment: downPayment,
      ...(paymentMethod ? { paymentmethod: paymentMethod.id } : {}),
      ...(installment ? { installment_count: installmentCount } : {}),
      tax: tax,
    };

    if (onSubmit) {
      const response = await onSubmit(param);
      setIsLoading(false);  // Stop loading
      if (response) {
        onClose();
      }
      return;
    }

    const response = await cqAPI.post(`/pay?_uid=${_uid}`, param);
    setIsLoading(false);  // Stop loading
    if (response.type === "error") {
      showToast(response.msg);
      return;
    }
    dialogManager.open({
      onDeny: () => {
      },
      acceptText: "OK",
      body: `The payment of $${total} was successfully captured.`,
      header: "Success",
    });

    onSave && onSave(response);
    onClose();
  };

  const handleRequestCreditCard = async () => {
    const result = await cqAPI.requestCreditCard({ skipSave: true }).catch(error => {
      console.error(error);
    });
    if (result) {
      setPaymentMethod(result);
    }
  };

  const handleSourceChange = async (e) => {
    if (e.target.value === 'change') {
      try {
        const result = await cqAPI.requestCreditCard({endpoint: `_uid=${_uid}`})
        if (result) {
          setLast4(result.card.last4);
          setSourceType(result.card.funding);
          setSourceTypes(readers.concat([
            {
              last4: result.card.last4,
              type: result.card.funding,
            },
          ]));
          setSelectedSource('card');
        }
      } catch (error) {
        console.error(error);
      }
      return;
    }
    if (e.target.value === 'card') {
      setSelectedSource('card');
      return;
    }
    setSelectedSource(e.target.value);
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm payment for {userProfileName}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {((installment && !highRisk) || !showSourceOnlyForInstallment) && (
              <VStack>
                <HStack justifyContent="space-between" mt={4}>
                  <Text>Payment Source: </Text>
                  <Box>
                    {!paymentMethod && readers.length ? (
                      <FormControl>
                        <Select value={selectedSource} onChange={handleSourceChange}>
                          {sourceTypes.filter(source=>!installment || !source.id).map((reader) => (
                            <option
                              key={reader.id || "card"}
                              value={reader.last4 ? "card" : reader.id}
                            >
                              {reader.last4
                                ? `Card ending in ${last4}`
                                : reader.label}
                            </option>
                          ))}
                          <option value={'change'}>{(readers.length + 1) === sourceTypes.length ? 'Update Card on File' : 'Add New Card'}</option>
                        </Select>
                      </FormControl>
                    ) : paymentMethod ? (
                      <>
                        <Text display="inline">
                          {Camelize(paymentMethod.card.funding)} card ending in{" "}
                          {paymentMethod.card.last4}
                        </Text>
                        <Button
                          marginLeft="10px"
                          variant="link"
                          onClick={() => setPaymentMethod(null)}
                        >
                          (Use Stored Card)
                        </Button>
                      </>
                    ) : sourceTypes.length ? (
                      <>
                        <Text display="inline">
                          {Camelize(sourceType)} card ending in {last4}
                        </Text>
                        <Button
                          marginLeft="10px"
                          variant="link"
                          onClick={() =>
                            cqAPI
                              .requestCreditCard({ endpoint: `_uid=${_uid}` })
                              .then((result) => {
                                if (result) {
                                  setLast4(result.card.last4);
                                  setSourceType(result.card.funding);
                                }
                              }).catch(error => console.error(error))
                          }
                        >
                          (Change)
                        </Button>
                      </>
                    ) : (
                      <>
                        <Text id="last4" display="inline">
                          {last4}
                        </Text>
                        <Button
                          id="ccchanger"
                          variant="link"
                          onClick={() =>
                            cqAPI
                              .requestCreditCard({ endpoint: `_uid=${_uid}` })
                              .then((result) => {
                                if (result) {
                                  setLast4(result.card.funding);
                                  setSourceType(result.card.funding);
                                  document.getElementById("ccchanger").textContent =
                                    " (Change)";
                                }
                              })
                              .catch(error => console.error(error))
                          }
                        >
                          Add card
                        </Button>
                      </>
                    )}
                  </Box>
                </HStack>
                <CCTestOptions
                  isAppInTestDebugMode={isTestMode}
                  isPaymentTestEnabled={isPaymentTestEnabled}
                  onPaymentChange={(e) => setTestCardNumber(e.target.value)}
                />
              </VStack>
            )}
            <form id="payment-form" onSubmit={handleSubmit}>
              <FormControl mt={4}>
                <FormLabel>Description <Text as="span" color="red.500"> * </Text></FormLabel>
                <Input
                  type="text"
                  placeholder="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </FormControl>
              <HStack mt={4}>
                <FormControl>
                  <FormLabel>Amount <Text as="span" color="red.500"> * </Text> </FormLabel>
                  <Input
                    type="number"
                    step=".01"
                    placeholder="Amount"
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Tax %</FormLabel>
                  <InputGroup>
                    <Input
                      type="number"
                      step=".0001"
                      placeholder="0"
                      pr="4.5rem"
                      onChange={(e) => setTax(Number(e.target.value))}
                    />
                    <InputRightElement width="4.5rem" pointerEvents="none">
                      <Text fontSize="sm" color="gray.500">${totalTax}</Text>
                    </InputRightElement>
                  </InputGroup>
                </FormControl>
                <Box textAlign="right">
                  <Text>Total: ${total}</Text>
                </Box>
              </HStack>
              <Divider my={4} />
              {!paymentMethod && !hideInstallment && (
                <HStack mt={4} justifyContent="space-between">
                  <FormControl display="flex" alignItems="center">
                    <FormLabel htmlFor="installment-switch" mb="0">
                      Installment
                    </FormLabel>
                    <Switch
                      id="installment-switch"
                      isChecked={installment}
                      onChange={() => setInstallment(!installment)}
                    />
                  </FormControl>
                  {installment && !hideInstallment && (
                    <FormControl display="flex" alignItems="center">
                      <FormLabel htmlFor="risk-switch" mb="0">
                        High Risk
                      </FormLabel>
                      <Switch
                        id="risk-switch"
                        isChecked={highRisk}
                        onChange={() => setHighRisk(!highRisk)}
                      />
                    </FormControl>
                  )}
                </HStack>
              )}
              {installment && !hideInstallment && (
                <>
                  {!highRisk && (
                    <Box mt={4}>
                      <HStack>
                        <FormControl>
                          <FormLabel>Down payment: $</FormLabel>
                          <Input
                            type="number"
                            placeholder="Down payment"
                            value={downPayment}
                            min={0}
                            step=".01"
                            onChange={(e) => setDownPayment(e.target.value)}
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel>Number of payments thereafter</FormLabel>
                          <Input
                            type="number"
                            placeholder="Number of payments"
                            value={installmentCount}
                            min={1}
                            onChange={(e) => setInstallmentCount(e.target.value)}
                          />
                        </FormControl>
                      </HStack>
                      <FormControl mt={4}>
                        <FormLabel>Treatment Start Date</FormLabel>
                        <Input
                          type="date"
                          value={treatmentStartDate}
                          onChange={(e) => setTreatmentStartDate(e.target.value)}
                        />
                      </FormControl>
                      <FormControl mt={4}>
                        <FormLabel>Payment Frequency</FormLabel>
                        <Select
                          value={paymentFrequency}
                          onChange={(e) => setPaymentFrequency(e.target.value)}
                        >
                          <option value="weekly">Weekly</option>
                          <option value="bi-weekly">Bi-Weekly</option>
                          <option value="monthly">Monthly</option>
                        </Select>
                      </FormControl>
                      <Box mt={4}>
                        <Text>
                          Amount to be paid on each interval: $
                          <span id="eachpayment">{eachPayment}</span>
                        </Text>
                      </Box>
                    </Box>
                  )}
                </>
              )}
              <Divider my={4} />
              {!installment && !hideFSA && (
                <Box mt={4}>
                  <HStack justifyContent="space-between" width="100%">
                    <Text>
                      Use a different card for a one off payment (Insurance, FSA,
                      etc)
                    </Text>
                    <IconButton
                      aria-label="Card detail entry"
                      icon={<FontAwesomeIcon icon={faChevronRight} />}
                      onClick={handleRequestCreditCard}
                      variant="ghost"
                      size="sm"
                      isDisabled={installment}
                    />
                  </HStack>
                </Box>
              )}
              <HStack justifyContent="space-between" mt={4}>
                <Button type="button" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" isDisabled={isLoading}>
                  {isLoading ? <Spinner size="sm" /> : payButtonText}
                </Button>
              </HStack>
            </form>
          </ModalBody>
        </ModalContent>
      </Modal>
      <CollectPaymentMethodModal
        isOpen={isCollectPaymentMethodModalOpen}
        onClose={() => setIsCollectPaymentMethodModalOpen(false)}
        onCancel={cancelCollectSetupIntentPaymentMethod}
      />
    </>
  );
};

class CreditCardModalManager {
  constructor() {
    this.dialogContainer = document.createElement("div");
    document.body.appendChild(this.dialogContainer);
    this.root = null;
  }

  async open({
    onSave,
    onClose = () => true,
    userProfileName,
    initialSource,
    _uid,
    onCardChange,
    onSubmit,
    showSourceOnlyForInstallment,
    payButtonText,
    hideFSA,
    hideInstallment,
    isTestMode = false
  }) {
    const closeHandler = () => {
      if (this.root) {
        this.root.unmount();
      }
      onClose();
    };

    const readers = await cqAPI.get("/settings/stripe").then((r) => r.data.readers);

    this.root = createRoot(this.dialogContainer);
    this.root.render(
      <ChakraProvider>
        <CreditCardModal
          isOpen={true}
          onClose={closeHandler}
          onSave={onSave}
          userProfileName={userProfileName}
          initialSource={initialSource}
          _uid={_uid}
          onCardChange={onCardChange}
          showSourceOnlyForInstallment={showSourceOnlyForInstallment}
          onSubmit={onSubmit}
          hideFSA={hideFSA}
          payButtonText={payButtonText}
          hideInstallment={hideInstallment}
          readers={readers}
          isTestMode={isTestMode}
        />
      </ChakraProvider>
    );
  }

  close() {
    if (this.root) {
      this.root.unmount();
    }
  }
}

const creditCardModalManager = new CreditCardModalManager();
export default creditCardModalManager;
